import {useDispatch, useSelector} from "react-redux";
import {useEffect, useState, useRef} from "react";
import {useNavigate} from "react-router-dom";

import { searchProducts, productsSelectors } from "../../features/products";

import {BrowserView, MobileView, isMobile} from "react-device-detect";
import { Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import './styles.css';

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);

    return debouncedValue;
}

const SearchResults = ({ isLoading, searchResults, debouncedSearch, onItemClick, navigate }) => {
    return (
        <>
            {isLoading && (
                <div className="search-loading-ctn">
                    <Spin indicator={<LoadingOutlined spin style={{ color: '#DC0202' }} />} />
                </div>
            )}

            {!isLoading && searchResults.length > 0 && (
                <div className="search-results-list">
                    {searchResults.map(product => (
                        <div
                            key={product.id}
                            className="search-result-item"
                            onClick={() => {
                                navigate(`/product/${product.id}`);
                                if (onItemClick) onItemClick();
                            }}
                        >
                            <img
                                src={product.images?.[0]?.src || product.main_image}
                                alt={product.name}
                                className="search-item-img"
                            />
                            <div className="search-item-name">{product.name}</div>
                            <div className="search-item-price-ctn">
                                <div className="search-item-price-actual">{Math.round(Number(product.price)).toLocaleString('ru-RU')}₽</div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {!isLoading && debouncedSearch && searchResults.length === 0 && (
                <div className="search-empty">Ничего не найдено</div>
            )}
        </>
    );
};

const DesktopSearchForm = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchContainerRef = useRef(null);

    const {
        selectSearchResults,
        selectIsSearchLoading
    } = productsSelectors;

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const searchResults = useSelector(selectSearchResults);
    const isLoading = useSelector(selectIsSearchLoading);

    useEffect(() => {
        if (debouncedSearch) {
            dispatch(searchProducts(debouncedSearch));
        }
    }, [debouncedSearch, dispatch]);

    useEffect(() => {
        function handleClickOutside(event) {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setSearch('');
            }
        }

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [searchContainerRef]);

    return (
        <BrowserView>
            <div className='header-search' style={{ position: 'relative' }} ref={searchContainerRef}>
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className='header-search-input'
                    type='text'
                    placeholder="Поиск товаров..."
                />
                <button className='header-search-btn' onClick={() => {
                    if (search.trim()) {
                        navigate(`/search?q=${encodeURIComponent(search.trim())}`);
                        setSearch('');
                    }
                }}>Найти</button>

                {search && (
                    <div className="search-results-dropdown">
                        <SearchResults
                            isLoading={isLoading}
                            searchResults={searchResults}
                            debouncedSearch={debouncedSearch}
                            navigate={navigate}
                            onItemClick={() => setSearch('')}
                        />
                    </div>
                )}
            </div>
        </BrowserView>
    );
};

const MobileSearchForm = ({ onClose }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchContainerRef = useRef(null);

    const {
        selectSearchResults,
        selectIsSearchLoading
    } = productsSelectors;

    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 300);
    const searchResults = useSelector(selectSearchResults);
    const isLoading = useSelector(selectIsSearchLoading);

    useEffect(() => {
        if (debouncedSearch) {
            dispatch(searchProducts(debouncedSearch));
        }
    }, [debouncedSearch, dispatch]);

    const handleItemClick = () => {
        setSearch('');
        if (onClose) {
            onClose();
        }
    };

    return (
        <div className="mobile-search-form" ref={searchContainerRef}>
            <div className="mobile-search-wrapper">
                <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="mobile-search-input"
                    type="text"
                    placeholder="Поиск товаров..."
                />
                <button className="mobile-search-btn" onClick={() => {
                    if (search.trim()) {
                        navigate(`/search?q=${encodeURIComponent(search.trim())}`);
                        setSearch('');
                        if (onClose) onClose();
                    }
                }}>Найти</button>
            </div>

            {search && (
                <div className="mobile-search-results">
                    <SearchResults
                        isLoading={isLoading}
                        searchResults={searchResults}
                        debouncedSearch={debouncedSearch}
                        onItemClick={handleItemClick}
                        navigate={navigate}
                    />
                </div>
            )}
        </div>
    );
};

const SearchForm = () => {
    return <DesktopSearchForm />;
};

export default SearchForm;
export { MobileSearchForm, DesktopSearchForm };